import { useMemo, useCallback } from 'react';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAgaveSourceSystem } from 'app/hooks/useAgaveSourceSystem';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { CodingColumnsConfig } from 'API';
import {
  getOrderedVisibleColumnIds,
  getCodingSystemType,
  getColumnLabel,
  EQUIPMENT_CODING_COLUMN_IDS,
  type CodingColumnId,
  type CodingSystemType,
  type AgaveSourceSystem,
} from 'app/util/columnVisibility';

export interface UseColumnVisibilityResult {
  /** Array of visible column IDs in admin-configured order */
  visibleColumnIds: CodingColumnId[];
  /** Helper to check if a specific column is visible */
  isVisible: (columnId: CodingColumnId) => boolean;
  /** Get the display label for a column (custom label or default) */
  getLabel: (columnId: CodingColumnId) => string;
  /** The determined coding system type */
  codingSystemType: CodingSystemType | null;
  /** Whether any coding columns should be shown */
  hasCodingColumns: boolean;
}

/**
 * Returns which coding columns are visible based on ERP type, Paidol settings, and user role.
 * Column IDs are returned in admin-configured order.
 */
export function useColumnVisibility(): UseColumnVisibilityResult {
  const { selectedPaidol, userRole } = useAppSelector(selectUserCompanies);
  const { currentSourceSystem } = useAgaveSourceSystem();
  // SEE-974: the equipment-coding columns (transactionType + EM trio) only exist
  // when the flag is on — mirror useCodingSystemConfig so Customize Columns (and
  // anything else on this hook) doesn't leak them flag-off on a Vista paidol.
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);

  const codingSystemType = useMemo(() => getCodingSystemType(selectedPaidol), [selectedPaidol]);

  const sourceSystem =
    codingSystemType === 'agave' ? (currentSourceSystem as AgaveSourceSystem | undefined) : undefined;

  const rawConfig = selectedPaidol?.codingColumnsConfig as CodingColumnsConfig | null | undefined;
  const configEnabled = rawConfig?.isEnabled !== false;
  const effectiveConfig = configEnabled ? rawConfig : null;

  const visibleColumnIds = useMemo((): CodingColumnId[] => {
    if (!selectedPaidol || !codingSystemType) {
      return [];
    }

    if (codingSystemType === 'jobCode') {
      return ['jobCode'];
    }

    // When config is disabled, pass a paidol copy with null config so all columns show with defaults
    const effectivePaidol = configEnabled ? selectedPaidol : { ...selectedPaidol, codingColumnsConfig: null };

    const ids = getOrderedVisibleColumnIds({
      paidol: effectivePaidol,
      userRole,
      codingSystemType,
      sourceSystem,
    });

    // Equipment coding columns (transactionType + EM trio) only when the flag is on.
    return isEquipmentCodingEnabled ? ids : ids.filter((id) => !EQUIPMENT_CODING_COLUMN_IDS.includes(id));
  }, [selectedPaidol, userRole, codingSystemType, sourceSystem, configEnabled, isEquipmentCodingEnabled]);

  const isVisible = useMemo(() => {
    const visibleSet = new Set(visibleColumnIds);
    return (columnId: CodingColumnId): boolean => visibleSet.has(columnId);
  }, [visibleColumnIds]);

  const getLabel = useCallback(
    (columnId: CodingColumnId): string => getColumnLabel(columnId, effectiveConfig),
    [effectiveConfig]
  );

  return {
    visibleColumnIds,
    isVisible,
    getLabel,
    codingSystemType,
    hasCodingColumns: visibleColumnIds.length > 0,
  };
}

export default useColumnVisibility;
